import axios from 'axios';

// Bare axios — NO auth Bearer, NO refresh interceptor.
const publicAxios = axios.create({ baseURL: '/api' });

export interface PublicQuoteItem {
  id: string;
  name: string;
  qty: number;
  unit: string;
  spec: string | null;
}

export type QuoteStatus = 'open' | 'submitted' | 'closed';

export interface PublicQuoteData {
  councilName: string;
  requestTitle: string;
  refNumber: string;
  deadline: string | null;
  vendorName: string;
  /** Issuer block, printed when the vendor has no letterhead image. */
  vendorAddress: string;
  vendorPhone: string;
  /** DD/MM/YYYY — the same stamp the generated quotation PDF carries. */
  issueDate: string;
  /** Fetch the bands from `letterheadUrl()` when true. */
  hasHeaderImage: boolean;
  hasFooterImage: boolean;
  /** The vendor's own footer line; empty when they have no letterhead. */
  letterheadFooterText: string;
  /** The council's body footer note; empty when the vendor has a letterhead. */
  footerNote: string;
  status: QuoteStatus;
  specDoc: { name: string; size: number } | null;
  items: PublicQuoteItem[];
}

export interface SubmitLine {
  requestItemId: string;
  unitPrice: number | null;
}

export interface SubmitPayload {
  /**
   * No longer sent: the ID-card field was removed from the pricing page, so the
   * invite token alone authorizes a public submission. The server still accepts
   * the key from older clients but ignores it.
   */
  idCardNumber?: string;
  confirmedDeclaration: boolean;
  lines: SubmitLine[];
}

export type SubmitErrorCode =
  | 'id_card_mismatch'
  | 'already_submitted'
  | 'request_closed'
  | 'no_prices'
  | 'declaration_required'
  | 'invalid_lines'
  | 'invalid_price'
  | 'not_found';

export interface SubmitError {
  code: SubmitErrorCode;
  message: string;
}

/**
 * URL of a letterhead band for this invite token. The server resolves the file
 * from the token alone — no filename travels in the URL.
 */
export function letterheadUrl(token: string, kind: 'header' | 'footer'): string {
  return `/api/public/quote/${encodeURIComponent(token)}/letterhead/${kind}`;
}

export const publicQuoteApi = {
  get: (token: string) =>
    publicAxios
      .get<PublicQuoteData>(`/public/quote/${token}`)
      .then((r) => r.data),

  submit: (token: string, payload: SubmitPayload) =>
    publicAxios
      .post<{ ok: boolean }>(`/public/quote/${token}`, payload)
      .then((r) => r.data),
};
